/* eslint-disable no-plusplus */
/* eslint-disable no-console */
import { parseString } from "xml2js";
import { blankAdpForm } from "./blankAdpForm";

function toString(thing, stripSpecial = false) {
  const string = `${thing}`;
  if (string === "undefined" || string === "null") return "";
  // The PDF gives these special chars as an example, there may be more that aren"t allowed
  return stripSpecial ? string.replace(/[%<=>[\]^|]/g, "") : string;
}

// Convert an XML value to an EASI value if found in a string or array
function firstMatch(arrayOrOther, options) {
  if (!Array.isArray(arrayOrOther)) {
    const value = toString(arrayOrOther.trim());
    // eslint-disable-next-line no-restricted-syntax
    for (const [search, returnValue] of options) {
      if (value === search) {
        return returnValue;
      }
    }
  } else {
    // eslint-disable-next-line no-restricted-syntax
    for (const [search, returnValue] of options) {
      if (arrayOrOther.includes(search)) {
        return returnValue;
      }
    }
  }
  return "";
}

function reasonForApplication(arrayOrOther) {
  return firstMatch(arrayOrOther, [
    ["firstAccess", "first-access"],
    ["another", "another-type"],
    ["modifications", "modifications-non-adp"],
    ["replacement", "replacement"],
    ["adjustments", "modifications-previous"],
  ])
}

// Normalize XML option to EASI option
function normalize(arrayOrOther, prefix) {
  return firstMatch(arrayOrOther, [
    ["yes", `${prefix}-yes`],
    ["no", `${prefix}-no`],
    ["na", `${prefix}-na`],
    ["one", `${prefix}-one`],
    ["two", `${prefix}-two`],
    ["three", `${prefix}-three`],
    ["four", `${prefix}-four`],
    ["none", `${prefix}-none`],
    ["standard", `${prefix}-standard`],
    ["anatomical", `${prefix}-anatomical`],
    ["cm", `${prefix}-cm`],
    ["inches", `${prefix}-in`],
    ["kg", `${prefix}-kg`],
    ["lbs", `${prefix}-lbs`],
    ["bilateral", `${prefix}-bilateral`],
    ["oneHand", `${prefix}-oneHand`],
    ["", null],
  ])
}

const hasValue = (data) => {
  let result = false;
  Object.keys(data).forEach(key => {
    if (data[key] !== "")
      result = result || true;
  });
  return result;
}

export class AdpForm {
  loadSection1 = (data) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section1));

    form["last-name"] = data.applicantLastname;
    form["first-name"] = data.applicantFirstname;
    form["middle-initial"] = data.applicantMiddleinitial;
    form["health-card"] = data.healthNo;
    form["version-code"] = data.versionNo;
    form.dob = data.DateOfBirth;
    const sex = firstMatch(data.sex, [
      ["male", "m"],
      ["female", "f"]
    ]);
    if (sex) form.sex.push(sex);
    form.LTCH = data.nameLTCH;
    form["unit-number"] = data.unitNo;
    form["street-number"] = data.streetNo;
    form["street-name"] = data.streetName;
    form["lot-concession-rural"] = data.rrRoute;
    form.city = data.city;
    form["postal-code"] = data.postalCode;
    form["home-tel"] = data.homePhone;
    form["business-tel"] = data.busPhone;
    form.extension = data.phoneExtension;
    form.benefits = firstMatch(data.confirmationOfBenefit.q1Yn, [
      ["yes", "y-benefits"],
      ["no", "n-benefits"],
      ["", null]
    ]);
    if (data.confirmationOfBenefit.q1Yn === "yes") {
      form["type-of-benefit"] = firstMatch(data.confirmationOfBenefit.q1Ifyes, [
        ["OWP", "owp"],
        ["ODSP", "odsp"],
        ["ACSD", "acsd"],
      ]);
    };
    if (data.confirmationOfBenefit.q2Yn === "yes" && data.confirmationOfBenefit.q3Yn === "yes") {
      form.coverage = ["wsib", "vac"];
    } else if (data.confirmationOfBenefit.q2Yn === "yes") {
      form.coverage = ["wsib"];
    } else if (data.confirmationOfBenefit.q3Yn === "yes") {
      form.coverage = ["vac"];
    };

    return form;
  }

  loadSection2 = (data) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section2));

    form["medical-condition"] = data.devicesandEligibility.condition;
    form["mobility-status"] = data.devicesandEligibility.status;

    // Mobility Equipment Previously Funded by ADP
    if (data.devicesandEligibility.none === "none") {
      form["previous-equipment"].push("prev-none");
    } else {
      if (data.devicesandEligibility.forearm === "forearmCrutches") form["previous-equipment"].push("prev-forearm-crutches");
      if (data.devicesandEligibility.wheeled === "wheeledWalker") form["previous-equipment"].push("prev-wheeled-walker");
      if (data.devicesandEligibility.manual === "manualWheelchair") form["previous-equipment"].push("prev-manual-wheelchair");
      if (data.devicesandEligibility.power === "powerWheelchair") form["previous-equipment"].push("prev-power-wheelchair");
      if (data.devicesandEligibility.addOn === "powerDevice") form["previous-equipment"].push("prev-power-addon");
      if (data.devicesandEligibility.scooter === "powerScooter") form["previous-equipment"].push("prev-power-scooter");
      if (data.devicesandEligibility.seating === "positioningDevices") form["previous-equipment"].push("prev-positioning-devices");
      if (data.devicesandEligibility.tiltSystem === "powerTilt") form["previous-equipment"].push("prev-power-tilt");
      if (data.devicesandEligibility.reclineSystem === "powerRecline") form["previous-equipment"].push("prev-power-recline");
      if (data.devicesandEligibility.legRests === "powerElevating") form["previous-equipment"].push("prev-power-elevating");
      if (data.devicesandEligibility.frame === "paediatricFrame") form["previous-equipment"].push("prev-paediatric-standing");
      if (data.devicesandEligibility.stroller === "paediatricStroller") form["previous-equipment"].push("prev-paediatric-specific");
    }

    // Device(s) Currently Required by the Applicant on an ongoing daily basis
    if (data.devicesandEligibility.deviceForearm === "forearmCrutches") form["devices-required"].push("req-forearm-crutches");
    if (data.devicesandEligibility.deviceWheeled === "wheeledWalker") form["devices-required"].push("req-wheeled-walker");
    if (data.devicesandEligibility.deviceManual === "manualwheelchairIndependent") form["devices-required"].push("req-manual-wheelchair-independent");
    if (data.devicesandEligibility.deviceAmbulation === "ambulationAidwheelchair") form["devices-required"].push("req-ambulation-aid-manual");
    if (data.devicesandEligibility.deviceDependent === "manualwheelchairDependent") form["devices-required"].push("req-manual-wheelchair-propulsion");
    if (data.devicesandEligibility.deviceDynamic === "tiltwheelchairIndependent") form["devices-required"].push("req-dynamic-tilt-independent");
    if (data.devicesandEligibility.manualDyanmic === "tiltwheelchairDependent") form["devices-required"].push("req-dynamic-tilt-propulsion");
    if (data.devicesandEligibility.manualWheelchair === "addonDevice") form["devices-required"].push("req-manual-wheelchair-power");
    if (data.devicesandEligibility.powerBase === "powerBaseonly") form["devices-required"].push("req-power-base");
    if (data.devicesandEligibility.powerScooter === "powerScooteronly") form["devices-required"].push("req-power-scooter");
    if (data.devicesandEligibility.ambulation === "aidAndPower") form["devices-required"].push("req-ambulation-aid-power");
    if (data.devicesandEligibility.positioning === "positioningDevices") form["devices-required"].push("req-positioning-devices");
    if (data.devicesandEligibility.highTech === "attachJustification") form["devices-required"].push("req-high-tech-power-base");
    if (data.devicesandEligibility.standingFrame === "paediatricFrame") form["devices-required"].push("req-paediatric-standing");
    if (data.devicesandEligibility.adpFunded === "modificationsPreviouslyADP") form["devices-required"].push("req-modifications-ADP");
    if (data.devicesandEligibility.nonADPFunded === "modificationsNonADP") form["devices-required"].push("req-modifications-non-ADP");

    return form;
  }

  loadSection3 = (data) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section3));

    form["applicant-or-agent"] = firstMatch(data.sig.person, [
      ["agent", "agent"],
      ["applicant", "applicant"],
    ]);
    form.date = data.sig.Date;

    // If the above signature is not that of the applicant, specify relationship to applicant and fill out contact information
    form.relationship = firstMatch(data.contact.relationship, [
      ["spouse", "spouse"],
      ["parent", "parent"],
      ["guardian", "guardian"],
      ["trustee", "trustee"],
      ["attorney", "attorney"],
    ]);
    form["last-name"] = data.contact.applicantLastname;
    form["first-name"] = data.contact.applicantFirstname;
    form["middle-initial"] = data.contact.applicantMiddleinitial;
    form["unit-number"] = data.contact.unitNo;
    form["street-number"] = data.contact.streetNo;
    form["street-name"] = data.contact.streetName;
    form["lot-concession-rural"] = data.contact.rrRoute;
    form.city = data.contact.city;
    form.province = data.contact.province;
    form["postal-code"] = data.contact.postalCode;
    form["primary-tel"] = data.contact.homePhone;
    form["business-tel"] = data.contact.busPhone;
    form.extension = data.contact.phoneExtension;

    return form;
  }

  loadSection2a = (data) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section2a));

    // Base Device
    if (data.none === "none") {
      form["base-device"].push("none");
    } else {
      const walker = firstMatch(data.walker, [
        ["adultWalkertype1", "awwt1"],
        ["adultWalkertype2", "awwt2"],
        ["adultWalkertype3", "awwt3"],
        ["paediatricWalkertype1", "pswwt1"],
        ["paediatricWalkertype2", "pswwt2"],
        ["paediatricWalkerframe", "pswwwf"],
      ]);
      if (walker) form["base-device"].push(walker);

      const paediatricFrame = firstMatch(data.paediatricFrame, [
        ["paediatricFrametype1", "psft1"],
        ["paediatricFrametype2", "psft2"],
      ]);
      form["base-device"].push(paediatricFrame);

      const forearmCrutches = firstMatch(data.forearmCrutches, [
        ["forearmCrutches", "forearm"],
      ]);
      if (forearmCrutches) form["base-device"].push(forearmCrutches);
    }

    // Reason for Application
    form["reason-for-application"] = reasonForApplication(data.reason);

    // Replacement Device(s) and/or Modifications Required Due To
    if (data.replacementStatus === "changeMS") form["replacement-devices"].push("2a-change-in-status");
    if (data.replacementSize === "changeBS") form["replacement-devices"].push("2a-change-in-size");
    if (data.replacementADP === "attachQuote") form["replacement-devices"].push("2a-worn-out");
    if (data.replacementSpecial === "attachLetter") form["replacement-devices"].push("2a-special-circumstances");

    // Confirmation of Applicant’s Eligibility for Ambulation Aids
    form["move-throughout-residence"] = normalize(data.confirmation1, "mt");
    form["move-beyond-residence"] = normalize(data.confirmation2, "mb");
    form["move-through-inaccessible"] = normalize(data.confirmation3, "in");
    form["independently-mobile"] = normalize(data.confirmation4, "im");
    form["requires-forearm-crutches"] = normalize(data.confirmation5, "fc");
    form["specific-standing-frame"] = normalize(data.confirmation6, "sf");

    // Prescription Details for Wheeled Walker Only
    form["seat-height"] = data.seatHeight;
    if (data.seatHeightmeasurement === "na") {
      form["seat-height-na"] = "2a-seatHeight-na"
    } else {
      form["seat-height-units"] = normalize(data.seatHeightmeasurement, "2a-seatHeight");
    };

    form["push-handle-height"] = data.handleHeight;
    form["push-handle-units"] = normalize(data.handleHeightmeasurement, "2a-pushHandle");
    form["hand-grips"] = normalize(data.handGrips, "hg");
    form["forearm-attachments"] = normalize(data.forearm, "fa");
    form["width-push-handles"] = data.widthHandles;
    form["width-between-units"] = normalize(data.widthHandlesmeasurement, "2a-widthBetween");
    form["client-weight"] = data.clientWeight;
    form["client-weight-units"] = normalize(data.clientWeightmeasurement, "2a-clientWeight");
    form.brakes = firstMatch(data.brakes, [
      ["none", "brake-none"],
      ["pushtoLock", "brake-pushToLock"],
      ["autoStop", "brake-auto-stop"],
    ]);
    form["brake-type"] = normalize(data.brakeType, "brakeType");
    form["number-of-wheels"] = normalize(data.noWheels, "nw");
    form["wheel-size"] = firstMatch(data.wheelSize, [
      ["4to6inches", "ws-4to6"],
      ["6to8inches", "ws-8to8"],
      ["8to10inches", "ws-8to10"],
    ]);
    form["back-support"] = normalize(data.backSupport, "bs");

    // Additional ADP Funded Options Required for Prescribed Device
    if (data.adpWalker === "paediatricWalker") form["additional-adp-funded-options"].push("adolescent-size-paediatric-specific");
    if (data.adpFrame === "paediatricWalkerframe") form["additional-adp-funded-options"].push("adolescent-size-paediatric-wheeled");
    if (data.adpStanding === "paediatricFrame") form["additional-adp-funded-options"].push("adolescent-size-paediatric-standing");

    // Non ADP Funded Options Prescribed
    for (let i = 1; i <= 9; i++) {
      if (data[`nonADP${i}`]) form["non-adp-funded-options-prescribed"].push(data[`nonADP${i}`]);
    }
    // if no data, set array to empty string to show at least 1 empty row (as default on screen)
    if (form["non-adp-funded-options-prescribed"].length === 0) form["non-adp-funded-options-prescribed"].push("");

    // Set Up Instructions for Vendor.
    for (let i = 1; i <= 18; i++) {
      if (data[`setup${i}`]) form["setup-instructions-for-vendor"].push(data[`setup${i}`]);
    }
    // if no data, set array to empty string to show at least 1 empty row (as default on screen)
    if (form["setup-instructions-for-vendor"].length === 0) form["setup-instructions-for-vendor"].push("");

    // Custom Modifications Required
    if (data.custom === "customModifications") {
      form["custom-modifications-required"] = "cm-yes";
    }
    form["custom-modifications-clinical-rationale"] = data.costLabour;

    return form;
  }

  loadSection2b = (data) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section2b));

    // Base Device
    form["base-device"] = firstMatch(data.baseDevice, [
      ["adultStdwheelchair", "asmw"],
      ["adultlightwtStdwheelchair", "alsmw"],
      ["adultlightwtPermwheelchair", "alpmw"],
      ["adultHighperfwheelchair", "ahprmw"],
      ["adultTiltwheelchair", "amdtw"],
      ["paediatriclightwtStdwheelchairlightwtStdwheelchair", "plsmw"],
      ["paediatriclightwtPermwheelchair", "plpmw"],
      ["paediatricHighperfwheelchair", "phprmw"],
      ["paediatricTiltwheelchair", "pmdtw"],
      ["paediatricStroller", "psss"],
      ["none", "none"],
    ]);
    if (data.powerAddOndevice === "powerAddondevice") form['power-addon'] = 'power-addon-requested';

    // Reason for Application
    form["reason-for-application"] = reasonForApplication(data.reason);

    // Replacement Device(s) and/or Modifications Required Due To
    if (data.replacementStatus === "changeMS") form["replacement-devices"].push("2b-change-in-status");
    if (data.replacementSize === "changeBS") form["replacement-devices"].push("2b-change-in-size");
    if (data.replacementADP === "attachQuote") form["replacement-devices"].push("2b-worn-out");
    if (data.replacementSpecial === "attachLetter") form["replacement-devices"].push("2b-special-circumstances");

    // Confirmation of Applicant’s Eligibility for A Manual Wheelchair
    form["move-throughout-residence"] = normalize(data.confirmation1, "mt");
    form["move-beyond-residence"] = normalize(data.confirmation2, "mb");
    form["move-throughout-dependent"] = normalize(data.confirmation3, "mtd");
    form["move-beyond-dependent"] = normalize(data.confirmation4, "mbd");
    form["move-independently-throughout"] = normalize(data.confirmation5, "mit"); // Titanium
    form["move-independently-beyond"] = normalize(data.confirmation6, "mib"); // Titanium
    form["weight-shift-independently"] = normalize(data.confirmation7, "wsi");
    form["history-tissue-trauma"] = normalize(data.confirmation8, "htt");
    form["maintain-functional-posture"] = normalize(data.confirmation9, "mfp");
    form["intolerance-sitting"] = normalize(data.confirmation10, "is");
    form["some-power-throughput"] = normalize(data.confirmation11, "spt");
    form["some-power-beyond"] = normalize(data.confirmation12, "spb");
    form.anticipated = normalize(data.confirmation13, "ant");

    // Prescription Details for Manual Wheelchair Only
    form["seat-width"] = data.seatWidth;
    form["seat-width-units"] = normalize(data.seatWidthmeasurement, "2b-seatWidth");
    form["seat-depth"] = data.seatDepth;
    form["seat-depth-units"] = normalize(data.seatDepthmeasurement, "2b-seatDepth");
    form["seat-to-floor"] = data.floorHeight;
    form["seat-to-floor-units"] = normalize(data.floorHeightmeasurement, "2b-seatToFloor");
    form["back-cane-height"] = data.caneHeight;
    form["back-cane-height-units"] = normalize(data.caneHeightmeasurement, "2b-backCane");
    form["back-height"] = data.backHeight;
    form["back-height-units"] = normalize(data.backHeightmeasurement, "2b-backHeight");
    form["leg-rest-length"] = data.restLength;
    form["leg-rest-length-units"] = normalize(data.restLengthmeasurement, "2b-legRest");
    form["client-weight"] = data.clientWeight;
    form["client-weight-units"] = normalize(data.clientWeightmeasurement, "2b-clientWeight");

    // Additional ADP Funded Options Required for Prescribed Manual Wheelchair
    if (data.adjustableTension === "backUpholstery") form["additional-adp-funded-options"].push("2b-atbu");
    if (data.heavyDuty === "heavyDuty") form["additional-adp-funded-options"].push("hdcbu");
    if (data.recliner === "reclinerOption") form["additional-adp-funded-options"].push("recliner-option");
    if (data.footplates === "angleAdjustable") form["additional-adp-funded-options"].push("angle-adjustable-footplates");
    if (data.legrests === "elevatingLegrests") form["additional-adp-funded-options"].push("elevating-legrests");
    if (data.spoke === "spokeProtectors") form["additional-adp-funded-options"].push("spoke-protectors");
    if (data.projected === "projectedHandrims") form["additional-adp-funded-options"].push("projected-handrims");
    if (data.standardManual === "wheelchairFrame") form["additional-adp-funded-options"].push("smwfwmdt");
    if (data.gradeAids === "gradeAids") form["additional-adp-funded-options"].push("grade-aids");
    if (data.casterPin === "casterPinlocks") form["additional-adp-funded-options"].push("caster-pin-locks");
    if (data.amputeeAxle === "amputeeAxleplates") form["additional-adp-funded-options"].push("amputee-axle-plates");
    if (data.quickRelease === "quickReleaseaxles") form["additional-adp-funded-options"].push("quick-release-axles");
    if (data.stroller === "stroller") form["additional-adp-funded-options"].push("stroller-handles");
    if (data.oxygen === "oxytankHolder") form["additional-adp-funded-options"].push("2b-oxygen-tank-holder");
    if (data.ventilator === "ventilatorTray") form["additional-adp-funded-options"].push("2b-ventilator-tray");
    if (data.titanium === "titaniumFrame") form["additional-adp-funded-options"].push("titanium-frame");
    if (data.clothingGuards === "clothingGuards") form["additional-adp-funded-options"].push("clothing-guards");
    if (data.oneArm === "oneArm") form["additional-adp-funded-options"].push("one-arm-lever-drive");
    if (data.uniLateral === "wheelLock") form["additional-adp-funded-options"].push("uni-lateral-wheel-lock");
    if (data.plastic === "coatedHandrims") form["additional-adp-funded-options"].push("plastic-coated-handrims");

    // Provide Clinical Rationale (if standardManual:wheelchairFrame or titanium:titaniumFrame checked)
    form["provide-clinical-rationale"] = data.rationale;

    // Non ADP Funded Options Prescribed
    for (let i = 1; i <= 9; i++) {
      if (data[`nonADP${i}`]) form["non-adp-funded-options-prescribed"].push(data[`nonADP${i}`]);
    }
    // if no data, set array to empty string to show at least 1 empty row (as default on screen)
    if (form["non-adp-funded-options-prescribed"].length === 0) form["non-adp-funded-options-prescribed"].push("");

    // Set Up Instructions for Vendor
    for (let i = 1; i <= 18; i++) {
      if (data[`setup${i}`]) form["setup-instructions-for-vendor"].push(data[`setup${i}`]);
    }
    // if no data, set array to empty string to show at least 1 empty row (as default on screen)
    if (form["setup-instructions-for-vendor"].length === 0) form["setup-instructions-for-vendor"].push("");

    // Custom Modifications Required
    if (data.custom === "customModifications") {
      form["custom-modifications-required"] = "cm-yes";
    }
    form["custom-modifications-clinical-rationale"] = data.costLabour;

    return form;
  }

  loadSection2c = (data) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section2c));

    // Base Device
    form["base-device"] = firstMatch(data.baseDevice, [
      ["adultType1", "apbt1"],
      ["adultType2", "apbt2"],
      ["adultType3", "apbt3"],
      ["paediatricType1", "ppbt1"],
      ["paediatricType2", "ppbt2"],
      ["paediatricType3", "ppbt3",],
      ["paediatricTilt", "ppbwmdt"],
      ["powerScooter", "power-scooter",],
      ["none", "none"],
    ]);

    // Reason for Application
    form["reason-for-application"] = reasonForApplication(data.reason);

    // Replacement Device(s) and/or Modifications Required Due To
    if (data.replacementStatus === "changeMS") form["replacement-devices"].push("2c-change-in-status");
    if (data.replacementSize === "changeBS") form["replacement-devices"].push("2c-change-in-size");
    if (data.replacementADP === "attachQuote") form["replacement-devices"].push("2c-worn-out");
    if (data.replacementSpecial === "attachLetter") form["replacement-devices"].push("2c-special-circumstances");

    // Confirmation of Applicant’s Eligibility for a Power Base
    form["base-move-throughout-residence"] = normalize(data.confirmation1, "bmt");
    form["base-move-beyond-residence"] = normalize(data.confirmation2, "bmb");

    // Confirmation of Applicant’s Eligibility for a Power Scooter
    form["scooter-move-throughout-residence"] = normalize(data.confirmation3, "smt");
    form["scooter-move-beyond-residence"] = normalize(data.confirmation4, "smb");
    form["scooter-seat-tiller"] = normalize(data.confirmation5, "sst");

    // Prescription Details for Power Device Only
    form["seat-width"] = data.seatWidth;
    form["seat-width-units"] = normalize(data.seatWidthmeasurement, "2c-seatWidth");
    form["finished-back-height"] = data.backHeight;
    form["finished-back-height-units"] = normalize(data.backHeightmeasurement, "2c-finishedBackHeight");
    form["finished-seat-to-floor-height"] = data.floorHeight;
    form["finished-seat-to-floor-units"] = normalize(data.floorHeightmeasurement, "2c-finishedSeatToFloor");
    form["leg-rest-length"] = data.restLength;
    form["leg-rest-length-units"] = normalize(data.restLengthmeasurement, "2c-legRestLength");
    form["seat-depth"] = data.seatDepth;
    form["seat-depth-units"] = normalize(data.seatDepthmeasurement, "2c-seatDepth");
    form["client-weight"] = data.clientWeight;
    form["client-weight-units"] = normalize(data.clientWeightmeasurement, "2c-clientWeight");

    // Additional ADP Funded Options Required for Prescribed Power Base
    if (data.adjustableTension === "backUpholstery") form["additional-adp-funded-options"].push("atbu");
    if (data.midline === "midlineControl") form["additional-adp-funded-options"].push("midline-control");
    if (data.manualRecline === "manualRecline") form["additional-adp-funded-options"].push("manual-recline-control");
    if (data.footplates === "angleAdjustable") form["additional-adp-funded-options"].push("aafp");
    if (data.legrests === "manualElevating") form["additional-adp-funded-options"].push("melp");
    if (data.swingaway === "mountingBracket") form["additional-adp-funded-options"].push("smb");
    if (data.onePiece === "onePiece") form["additional-adp-funded-options"].push("op9090fr");
    if (data.seatPackage1 === "seatPackage1") form["additional-adp-funded-options"].push("seat-package-1");
    if (data.seatPackage2 === "seatPackage2") form["additional-adp-funded-options"].push("seat-package-2");
    if (data.oxygen === "oxytankHolder") form["additional-adp-funded-options"].push("oxygen-tank-holder");
    if (data.ventilator === "ventilatorTray") form["additional-adp-funded-options"].push("ventilator-tray");

    // Provide clinical rationale for the following Specialty Components
    if (data.spControls1 === "controls1") form["speciality-options"].push("specialty-1");
    if (data.spControls2 === "controls2") form["speciality-options"].push("specialty-2");
    if (data.spControls3 === "controls3") form["speciality-options"].push("specialty-3");
    if (data.spControls4 === "controls4") form["speciality-options"].push("specialty-4");
    if (data.spControls5 === "controls5") form["speciality-options"].push("specialty-5");
    if (data.spControls6 === "controls6") form["speciality-options"].push("specialty-6");
    if (data.autoCorrection === "autoCorrection") form["speciality-options"].push("specialty-auto-correction-system");
    form["medical-condition"] = data.rationale;

    // Provide clinical rationale for the following Power Positioning Devices in Justification for Funding Chart
    if (data.powerTilt === "tilt") form["power-positioning-devices"].push("ppd-1");
    if (data.powerRecline === "recline") form["power-positioning-devices"].push("ppd-2");
    if (data.tiltAndRecline === "tiltRecline") form["power-positioning-devices"].push("ppd-3");
    if (data.powerElevating === "elevatingFootrests") form["power-positioning-devices"].push("ppd-4");
    if (data.ControlBox === "controlBox") form["power-positioning-devices"].push("ppd-5");

    // Non ADP Funded Options Prescribed
    for (let i = 1; i <= 9; i++) {
      if (data[`nonADP${i}`]) form['non-adp-funded-options-prescribed'].push(data[`nonADP${i}`]);
    }
    // if no data, set array to empty string to show at least 1 empty row (as default on screen)
    if (form["non-adp-funded-options-prescribed"].length === 0) form["non-adp-funded-options-prescribed"].push("");

    // Set Up Instructions for Vendor
    for (let i = 1; i <= 18; i++) {
      if (data[`setup${i}`]) form['setup-instructions-for-vendor'].push(data[`setup${i}`]);
    }
    // if no data, set array to empty string to show at least 1 empty row (as default on screen)
    if (form["setup-instructions-for-vendor"].length === 0) form["setup-instructions-for-vendor"].push("");

    // Custom Modifications Required
    if (data.custom === "customModifications") {
      form["custom-modifications-required"] = "cm-yes";
    }
    form["custom-modifications-clinical-rationale"] = data.costLabour;

    return form;
  }

  loadSection2d = (data) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section2d));

    // // Devices and Options
    if (data.seatM === "modular") form["seat-cushion"].push("sc-modular");
    if (data.seatCF === "customFabricated") form["seat-cushion"].push("sc-custom");
    if (data.coverM === "modular") form["seat-cushion-cover"].push("scc-modular");
    if (data.coverCF === "customFabricated") form["seat-cushion-cover"].push("scc-custom");
    if (data.optionM === "modular") form["seat-options"].push("so-modular");
    if (data.optionCF === "customFabricated") form["seat-options"].push("so-custom");
    if (data.hardwareM === "modular") form["seat-hardware"].push("sh-modular");
    if (data.hardwareCF === "customFabricated") form["seat-hardware"].push("sh-custom");
    if (data.adductorM === "modular") form["pommel-adductors"].push("pa-modular");
    if (data.adductorCF === "customFabricated") form["pommel-adductors"].push("pa-custom");
    if (data.pommelCF === "customFabricated") form["pommel-hardware"] = "ph-custom";
    if (data.backM === "modular") form["back-support"].push("bs-modular");
    if (data.backCF === "customFabricated") form["back-support"].push("bs-custom");
    if (data.supportoptionM === "modular") form["back-support-options"].push("bso-modular");
    if (data.supportoptionCF === "customFabricated") form["back-support-options"].push("bso-custom");
    if (data.backcoverCF === "customFabricated") form["back-cover"] = "bc-custom";
    if (data.backHardwareM === "modular") form["back-hardware"].push("bh-modular");
    if (data.backHardwareCF === "customFabricated") form["back-hardware"].push("bh-custom");
    if (data.completeM === "modular") form["complete-assembly"].push("ca-modular");
    if (data.completeCF === "customFabricated") form["complete-assembly"].push("ca-custom");
    if (data.headrestM === "modular") form["headrest-neckrest"].push("hn-modular");
    if (data.headrestCF === "customFabricated") form["headrest-neckrest"].push("hn-custom");
    if (data.headoptionCF === "customFabricated") form["headrest-neckrest-options"] = "hno-custom";
    if (data.headhardwareM === "modular") form["headrest-neckrest-hardware"].push("hnh-modular");
    if (data.headhardwareCF === "customFabricated") form["headrest-neckrest-hardware"].push("hnh-custom");
    if (data.beltM === "modular") form["positioning-belts"].push("pb-modular");
    if (data.beltCF === "customFabricated") form["positioning-belts"].push("pb-custom");
    if (data.beltoptionCF === "customFabricated") form["positioning-belt-options"] = "pbo-custom";
    if (data.armsupportM === "modular") form["arm-support"].push("as-modular");
    if (data.armsupportCF === "customFabricated") form["arm-support"].push("as-custom");
    if (data.armoptionM === "modular") form["arm-support-options"].push("aso-modular");
    if (data.armoptionCF === "customFabricated") form["arm-support-options"].push("aso-custom");
    if (data.armhardwareM === "modular") form["arm-support-hardware"].push("ash-modular");
    if (data.armhardwareCF === "customFabricated") form["arm-support-hardware"].push("ash-custom");
    if (data.trayM === "modular") form.tray.push("tray-modular");
    if (data.trayCF === "customFabricated") form.tray.push("tray-custom");
    if (data.trayoptionM === "modular") form["tray-options"].push("to-modular");
    if (data.trayoptionCF === "customFabricated") form["tray-options"].push("to-custom");
    if (data.lateralsupportM === "modular") form["lateral-support"].push("ls-modular");
    if (data.lateralsupportCF === "customFabricated") form["lateral-support"].push("ls-custom");
    if (data.lateraloptionCF === "customFabricated") form["lateral-support-options"] = "lso-custom";
    if (data.lateralhardwareCF === "customFabricated") form["lateral-support-hardware"] = "lsh-custom";
    if (data.footsupportM === "modular") form["foot-leg-support"].push("fls-modern"); // Note: fls-modern is a typo but we're stuck with it now
    if (data.footsupportCF === "customFabricated") form["foot-leg-support"].push("fls-custom");
    if (data.footoptionM === "modular") form["foot-leg-support-options"].push("flso-modular");
    if (data.footoptionCF === "customFabricated") form["foot-leg-support-options"].push("flso-custom");
    if (data.foothardwareM === "modular") form["foot-leg-support-hardware"].push("flsh-modern"); // Note: flsh-modern is a typo but we're stuck with it now
    if (data.foothardwareCF === "customFabricated") form["foot-leg-support-hardware"].push("flsh-custom");

    // Reason for Application
    form["reason-for-application"] = reasonForApplication(data.reason);

    // Replacement Device(s) and/or Modifications Required Due To
    if (data.replacementStatus === "changeMS") form["replacement-devices"].push("2d-change-in-status");
    if (data.replacementSize === "changeBS") form["replacement-devices"].push("2d-change-in-size");
    if (data.replacementADP === "equipment") form["replacement-devices"].push("2d-worn-out");
    if (data.replacementSpecial === "attachLetter") form["replacement-devices"].push("2d-special-circumstances");

    // Confirmation of Applicant’s Eligibility for a Positioning Devices – Seating
    form["seating-components"] = normalize(data.confirmation1, "sc");
    form["requires-tray"] = normalize(data.confirmation2, "tray");

    // Non ADP Funded Options Prescribed
    for (let i = 1; i <= 9; i++) {
      if (data[`nonADP${i}`]) form['non-adp-funded-options-prescribed'].push(data[`nonADP${i}`]);
    }
    // if no data, set array to empty string to show at least 1 empty row (as default on screen)
    if (form["non-adp-funded-options-prescribed"].length === 0) form["non-adp-funded-options-prescribed"].push("");

    // Set Up Instructions for Vendor
    for (let i = 1; i <= 18; i++) {
      if (data[`setup${i}`]) form['setup-instructions-for-vendor'].push(data[`setup${i}`]);
    }
    // if no data, set array to empty string to show at least 1 empty row (as default on screen)
    if (form["setup-instructions-for-vendor"].length === 0) form["setup-instructions-for-vendor"].push("");

    // Custom Modifications Required
    if (data.custom === "customModifications") {
      form["custom-modifications-required"] = "cm-yes";
    }
    form["custom-modifications-clinical-rationale"] = data.costLabour;

    return form;
  }

  loadSection4a = (data, info) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section4a));

    form.attachments.push(data["attachment-justification-funding-chart"] ? "4-attachment-justification-funding-chart" : null);
    form.attachments.push(data["attachment-special-circumstances"] ? "4-attachment-special-circumstances" : null);
    form.attachments.push(data["attachment-vendor-quote-custom-mods"] ? "4-attachment-vendor-quote-custom-mods" : null);
    form.attachments.push(data["attachment-vendor-quote-replacement"] ? "4-attachment-vendor-quote-replacement" : null);

    form.sections.push(data["section1-touched"] ? "4-s1" : null);
    form.sections.push(data["section2a-show"] ? "4-s2a" : null);
    form.sections.push(data["section2b-show"] ? "4-s2b" : null);
    form.sections.push(data["section2c-show"] ? "4-s2c" : null);
    form.sections.push(data["section2d-show"] ? "4-s2d" : null);
    form.sections.push(data["section3-touched"] ? "4-s2s4" : null);

    form.sections.push(info["section1-touched"] ? "4-s1" : null);
    form.sections.push(info["section2a-show"] ? "4-s2a" : null);
    form.sections.push(info["section2b-show"] ? "4-s2b" : null);
    form.sections.push(info["section2c-show"] ? "4-s2c" : null);
    form.sections.push(info["section2d-show"] ? "4-s2d" : null);
    form.sections.push(info["section3-touched"] ? "4-s2s4" : null);

    form.attachments.push(data["attachment-justification-funding-chart"] ? "4-attachment-justification-funding-chart" : null);
    form.attachments.push(data["attachment-special-circumstances"] ? "4-attachment-special-circumstances" : null);
    form.attachments.push(data["attachment-vendor-quote-custom-mods"] ? "4-attachment-vendor-quote-custom-mods" : null);
    form.attachments.push(data["attachment-vendor-quote-replacement"] ? "4-attachment-vendor-quote-replacement" : null);

    return form;
  }

  loadSection4 = (data, info) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched
    const form = JSON.parse(JSON.stringify(blankAdpForm.section4));

    form.s1 = Boolean(data.noteToADP.section1) || info["section1-touched"];
    form.s2a = Boolean(data.noteToADP.section2a) || info["section2a-show"];
    form.s2b = Boolean(data.noteToADP.section2b) || info["section2b-show"];
    form.s2c = Boolean(data.noteToADP.section2c) || info["section2c-show"];
    form.s2d = Boolean(data.noteToADP.section2d) || info["section2d-show"];
    form.s3s4 = Boolean(data.noteToADP.section3and4) || info["section3-touched"];

    form["attachment-justification-funding-chart"] = Boolean(data.noteToADP.fundingChart);
    form["attachment-special-circumstances"] = Boolean(data.noteToADP.letter);
    form["attachment-vendor-quote-custom-mods"] = Boolean(data.noteToADP.vendorCustom);
    form["attachment-vendor-quote-replacement"] = Boolean(data.noteToADP.vendorReplacement);

    return form;
  }

  loadSection4Alt = (data) => {
    const form = JSON.parse(JSON.stringify(blankAdpForm.section4Alt));

    form.sections.push(data.s1 ? "4-s1" : null);
    form.sections.push(data.s2a ? "4-s2a" : null);
    form.sections.push(data.s2b ? "4-s2b" : null);
    form.sections.push(data.s2c ? "4-s2c" : null);
    form.sections.push(data.s2d ? "4-s2d" : null);
    form.sections.push(data.s3s4 ? "4-s3s4" : null);

    form.attachments.push(data["attachment-justification-funding-chart"] ?  "4-attachment-justification-funding-chart" : null);
    form.attachments.push(data["attachment-special-circumstances"] ? "4-attachment-special-circumstances" : null);
    form.attachments.push(data["attachment-vendor-quote-custom-mods"] ? "4-attachment-vendor-quote-custom-mods" : null);
    form.attachments.push(data["attachment-vendor-quote-replacement"] ? "4-attachment-vendor-quote-replacement" : null);

    return form;
  }

  loadFormXml = (xml) => {
    // IMPORTANT: following the same behavior from screen: fields appears in data_json only when they are touched

    try {
      let data = "";
      parseString(xml, { explicitArray: false }, (err, result) => {
        data = result;
      });

      const form = {};

      // When starting an ADP Form, only section1 is present.
      // If section1 is not in XML file, format is invalid
      if (data.form1.Form?.section1) {
        form.section1 = JSON.parse(JSON.stringify(blankAdpForm.section1));;
        form.section1 = this.loadSection1(data.form1.Form.section1);
      } else {
        return undefined;
      }

      // If section2 or section3 is in XML file, all sections will be present with null or blank values
      if (data.form1.Form?.section2 || data.form1.Form?.section3 || data.form1.Form?.section4) {
        form.info = JSON.parse(JSON.stringify(blankAdpForm.info));
        form.section2 = JSON.parse(JSON.stringify(blankAdpForm.section2));
        form.section2a = JSON.parse(JSON.stringify(blankAdpForm.section2a));
        form.section2b = JSON.parse(JSON.stringify(blankAdpForm.section2b));
        form.section2c = JSON.parse(JSON.stringify(blankAdpForm.section2c));
        form.section2d = JSON.parse(JSON.stringify(blankAdpForm.section2d));
        form.section3 = JSON.parse(JSON.stringify(blankAdpForm.section3));
        form.section4 = JSON.parse(JSON.stringify(blankAdpForm.section4));
        form.section4Alt = JSON.parse(JSON.stringify(blankAdpForm.section4Alt));
        form.info["section1-touched"] = true;

        // If section2 is in XML file, info.section2 will be touched
        if (data.form1.Form?.section2) {
          form.section2 = this.loadSection2(data.form1.Form.section2);
          form.info["section2-touched"] = true;

          // Because XML probably will not be written according XML format RFC, need to check if all keys are empty
          if (data.form1.Form?.section2?.section2a && hasValue(data.form1.Form?.section2?.section2a)) {
            form.section2a = this.loadSection2a(data.form1.Form.section2.section2a);
            form.info["section2a-touched"] = true;
            form.info["section2a-show"] = true;
          };

          // Because XML probably will not be written according XML format RFC, need to check if all keys are empty
          if (data.form1.Form?.section2?.section2b && hasValue(data.form1.Form?.section2?.section2b)) {
            form.section2b = this.loadSection2b(data.form1.Form.section2.section2b);
            form.info["section2b-touched"] = true;
            form.info["section2b-show"] = true;
          };

          // Because XML probably will not be written according XML format RFC, need to check if all keys are empty
          if (data.form1.Form?.section2?.section2c && hasValue(data.form1.Form?.section2?.section2c)) {
            form.section2c = this.loadSection2c(data.form1.Form.section2.section2c);
            form.info["section2c-touched"] = true;
            form.info["section2c-show"] = true;
          };

          // Because XML probably will not be written according XML format RFC, need to check if all keys are empty
          if (data.form1.Form?.section2?.section2d && hasValue(data.form1.Form?.section2?.section2d)) {
            form.section2d = this.loadSection2d(data.form1.Form.section2.section2d);
            form.info["section2d-touched"] = true;
            form.info["section2d-show"] = true;
          };
        };

        // Because XML probably will not be written according XML format RFC, need to check if all keys are empty
        if (data.form1.Form?.section3 && hasValue(data.form1.Form?.section3)) {
          form.section3 = this.loadSection3(data.form1.Form.section3);
          form.info["section3-touched"] = true;
        };

        // Because XML probably will not be written according XML format RFC, need to check if all keys are empty
        if (data.form1.Form?.section4 && hasValue(data.form1.Form?.section4)) {
          form.section4 = this.loadSection4(data.form1.Form.section4, form.info);
        };

        form.section4Alt = this.loadSection4Alt(form.section4);
      };

      return form;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      return undefined;
    }
  }
}
